import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ToolPrepPopup from "../../../shared/Popup/ToolPrepPopup";
import ToolPrepSourceCards from "../../../shared/ToolPrepSourceCards";
import { useSyncScrollbarWidth } from "../../../hooks/useSyncScrollbarWidth";
import DocumentFileSourceCard from "../../shared/DocumentFileSourceCard";
import DocumentPageThumbnails from "../../shared/DocumentPageThumbnails";
import { useDocumentFileSource } from "../../shared/useDocumentFileSource";
import { bakeSignedPdf, downloadSignedPdf } from "./bakeSignedPdf";
import { PDF_WATERMARK_MAX_PAGES } from "../../shared/renderPdfFirstPage";
import {
  TOOL_PREP_PREVIEW_BG,
  TOOL_PREP_PREPARING_PREVIEW_LABEL,
} from "../../../../../utils/editorEntrySession";
import {
  DOCUMENT_PREP_STAGE_INSET,
  TOOL_PREP_STAGE_INSET,
} from "../../../lib/toolPrepImageLayout";
import SignPdfControls from "./SignPdfControls";
import SignPdfFieldToolbar from "./SignPdfFieldToolbar";
import SignPdfSignatureToolbar from "./SignPdfSignatureToolbar";
import SignPdfPreview from "./SignPdfPreview";
import SignPdfThumbMarks from "./SignPdfThumbMarks";
import SignPdfSignToolbar from "./SignPdfSignToolbar";
import SignPdfFinishModal from "./SignPdfFinishModal";
import SignPdfFinishThankYouModal from "./SignPdfFinishThankYouModal";
import { SIGN_PDF_FINISH_THANK_YOU_ACTIONS } from "./signPdfFinishThankYou";
import {
  getWatermarkFontStack,
} from "../../../watermark/Popup/watermarkSettings";
import {
  createDefaultSignPdfSession,
  DEFAULT_SIGN_PDF_SESSION,
  getSignPdfPageScope,
  hasAnySignPdfMark,
  hasSignPdfSigningIntent,
  SIGN_PDF_MODES,
  SIGN_PDF_RENDER_SCALE,
  SIGN_PDF_SCOPES,
  signPdfModeFromSettings,
} from "./signPdfSettings";
import {
  addSignPdfPlacement,
  canPlaceSignPdfField,
  getActiveResolvedSignPdfMark,
  getActiveSignPdfPlacement,
  getSignPdfPageSigningStatus,
  listAllResolvedSignPdfMarks,
  resolveSignPdfMarksForPage,
  addSignPdfSigningBlock,
  SIGN_PDF_FIELD_KINDS,
} from "./signPdfPlacements";
import {
  parseSignPdfBlockPayload,
} from "./signPdfBlockCatalog";
import {
  deriveSignPdfWorkflowStep,
  getSignPdfSetupChecklist,
  signPdfWorkflowHint,
  SIGN_PDF_WORKFLOW_STEPS,
} from "./signPdfWorkflow";
import {
  areAllSignPdfPagesConfirmed,
  getSignPdfPageRailState,
} from "./signPdfPageRail";
import "./SignPdfToolPrep.css";

async function ensureSignPdfFontsForBake(session) {
  if (typeof document === "undefined" || !document.fonts?.load) return;
  const marks = listAllResolvedSignPdfMarks(session);
  const loads = [];
  for (const mark of marks) {
    const mode = signPdfModeFromSettings(mark);
    if (mode === SIGN_PDF_MODES.UPLOAD || mode === SIGN_PDF_MODES.DRAW) {
      continue;
    }
    if (mark?.logoUrl) continue;
    const stack = getWatermarkFontStack(mark.fontId);
    if (!stack) continue;
    loads.push(document.fonts.load(`400 64px ${stack}`));
    loads.push(document.fonts.load(`italic 400 64px ${stack}`));
  }
  if (loads.length) {
    await Promise.allSettled(loads);
  }
  try {
    await document.fonts.ready;
  } catch {
    // ignore
  }
}

/**
 * Sign PDF — guided workflow: setup → format → place → review → finish.
 */
const SignPdfToolPrep = ({ onCancel = () => {}, onDone = null }) => {
  const scrollBodyRef = useRef(null);
  const actionsRef = useRef(null);
  const [session, setSession] = useState(DEFAULT_SIGN_PDF_SESSION);
  const [compareMode, setCompareMode] = useState("preview");
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(null);
  const [inReview, setInReview] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishCompleted, setFinishCompleted] = useState(false);
  const [reviewMarkedComplete, setReviewMarkedComplete] = useState(false);
  const [finishThankYouAction, setFinishThankYouAction] = useState(null);
  const [pageConfirmedMap, setPageConfirmedMap] = useState({});
  const [allPagesConfirmed, setAllPagesConfirmed] = useState(false);
  const [popdownReopenToken, setPopdownReopenToken] = useState(0);
  const [autoOpenFields, setAutoOpenFields] = useState(false);
  const [controlsKey, setControlsKey] = useState(0);
  const [placementDragging, setPlacementDragging] = useState(false);
  const [previewStageSize, setPreviewStageSize] = useState({
    width: 420,
    height: 420,
  });
  const prevPlacementCountRef = useRef(0);
  const pendingReviewAfterDoneRef = useRef(false);

  const {
    inputRef,
    accept,
    file,
    pdfFile,
    fileName,
    fileSizeLabel,
    previewUrl,
    pageUrls,
    pageCount,
    currentPage,
    loadingPreview,
    loadingPage,
    error,
    hasFile,
    clear,
    openPicker,
    onInputChange,
    onDropFiles,
    ensurePageUrl,
    goToPage,
    uploadProgress,
  } = useDocumentFileSource({
    acceptKind: "pdf",
    busy: exporting,
    previewScale: SIGN_PDF_RENDER_SCALE,
  });

  useSyncScrollbarWidth(scrollBodyRef);

  const pageNum = currentPage || 1;
  const activePlacement = getActiveSignPdfPlacement(session);
  const activeMark = getActiveResolvedSignPdfMark(session, pageNum);
  const pageScope = getSignPdfPageScope(session);
  const hasPreview = Boolean(previewUrl && uploadProgress == null);

  const placementCount = (session?.placements || []).length;
  const cappedPageCount = Math.min(pageCount, PDF_WATERMARK_MAX_PAGES);

  const allPagesComplete = useMemo(
    () =>
      areAllSignPdfPagesConfirmed(session, {
        pageConfirmedMap,
        allPagesConfirmed,
        pageCount: cappedPageCount,
      }),
    [session, pageConfirmedMap, allPagesConfirmed, cappedPageCount]
  );

  const getPageRailState = useCallback(
    (p) =>
      getSignPdfPageRailState(session, p, {
        currentPage: pageNum,
        pageConfirmedMap,
        allPagesConfirmed,
      }),
    [session, pageNum, pageConfirmedMap, allPagesConfirmed]
  );

  const workflowStep = useMemo(() => {
    if (finishOpen) return SIGN_PDF_WORKFLOW_STEPS.FINISH;
    if (inReview) return SIGN_PDF_WORKFLOW_STEPS.REVIEW;
    return deriveSignPdfWorkflowStep(session, { hasDocument: hasPreview });
  }, [session, inReview, finishOpen, hasPreview]);

  useEffect(() => {
    if (!inReview) setReviewMarkedComplete(false);
  }, [inReview]);

  const setupChecklistComplete = useMemo(
    () => getSignPdfSetupChecklist(session).complete,
    [session]
  );

  const placeField = useCallback(
    (kind, patch = {}) => {
      if (actionsRef.current?.placeField) {
        actionsRef.current.placeField(kind, patch);
        return;
      }
      setSession((prev) => {
        if (!hasSignPdfSigningIntent(prev)) return prev;
        if (!canPlaceSignPdfField(prev, kind)) return prev;
        return addSignPdfPlacement(prev, kind, pageNum, patch);
      });
    },
    [pageNum]
  );

  const placeSigningBlock = useCallback(
    (payload = "classic:standard") => {
      setSession((prev) => {
        if (!hasSignPdfSigningIntent(prev)) return prev;
        if (!canPlaceSignPdfField(prev, SIGN_PDF_FIELD_KINDS.SIGNATURE)) {
          return prev;
        }
        const { domainId, sizeId } = parseSignPdfBlockPayload(payload);
        return addSignPdfSigningBlock(prev, pageNum, {
          domainId,
          presetId: sizeId,
        });
      });
    },
    [pageNum]
  );

  const handleClear = () => {
    clear();
    setSession(createDefaultSignPdfSession());
    setCompareMode("preview");
    setExportError(null);
    setExportSuccess(null);
    setExportProgress(null);
    setInReview(false);
    setFinishOpen(false);
    setFinishCompleted(false);
    setReviewMarkedComplete(false);
    setFinishThankYouAction(null);
    setPageConfirmedMap({});
    setAllPagesConfirmed(false);
    setPopdownReopenToken(0);
    setAutoOpenFields(false);
    setPlacementDragging(false);
    setControlsKey((k) => k + 1);
    prevPlacementCountRef.current = 0;
  };

  const runBake = async () => {
    const bakeSource = pdfFile || file;
    if (!bakeSource) throw new Error("No PDF file");
    await ensureSignPdfFontsForBake(session);
    return bakeSignedPdf(
      bakeSource,
      (bakePage) => resolveSignPdfMarksForPage(session, bakePage, pageNum),
      {
        maxPages: PDF_WATERMARK_MAX_PAGES,
        fileNameSuffix: "signed",
        onProgress: setExportProgress,
      }
    );
  };

  const handleDone = async () => {
    if (!hasPreview) {
      setFinishOpen(true);
      return;
    }
    if (
      !hasAnySignPdfMark(session) &&
      !(session?.placements || []).length
    ) {
      setExportError("Add at least one signature or field before finishing.");
      setFinishOpen(true);
      return;
    }
    setFinishOpen(true);
  };

  const completeFinishAction = useCallback((action) => {
    setFinishOpen(false);
    setFinishCompleted(true);
    setFinishThankYouAction(action);
  }, []);

  const handleDownload = async () => {
    const bakeSource = pdfFile || file;
    if (!bakeSource || exporting) return;
    if (!hasAnySignPdfMark(session) && !(session?.placements || []).length) {
      setExportError("Add at least one signature or field before downloading.");
      return;
    }

    setExporting(true);
    setExportError(null);
    setExportSuccess(null);
    setExportProgress({ page: 0, total: 0 });

    try {
      const result = await downloadSignedPdf(
        bakeSource,
        (bakePage) => resolveSignPdfMarksForPage(session, bakePage, pageNum),
        {
          maxPages: PDF_WATERMARK_MAX_PAGES,
          fileNameSuffix: "signed",
          onProgress: setExportProgress,
        }
      );
      setExportSuccess(result.fileName);
      onDone?.(result);
      completeFinishAction(SIGN_PDF_FINISH_THANK_YOU_ACTIONS.DOWNLOAD);
    } catch (err) {
      setExportError(err?.message || "Could not sign PDF.");
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const handleDownloadLater = () => {
    if (exporting) return;
    completeFinishAction(SIGN_PDF_FINISH_THANK_YOU_ACTIONS.DOWNLOAD_LATER);
  };

  const handlePrint = async () => {
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const result = await runBake();
      const url = URL.createObjectURL(result.blob);
      const win = window.open(url, "_blank");
      if (win) {
        win.addEventListener("load", () => {
          try {
            win.print();
          } catch {
            // user may block popups
          }
        });
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      completeFinishAction(SIGN_PDF_FINISH_THANK_YOU_ACTIONS.PRINT);
    } catch (err) {
      setExportError(err?.message || "Could not prepare PDF for printing.");
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const handleEmailPdf = async () => {
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const result = await runBake();
      const subject = encodeURIComponent(`Signed PDF: ${result.fileName}`);
      const body = encodeURIComponent(
        "Please attach the signed PDF from your downloads."
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
      completeFinishAction(SIGN_PDF_FINISH_THANK_YOU_ACTIONS.EMAIL);
    } catch (err) {
      setExportError(err?.message || "Could not prepare PDF for email.");
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const handleShare = async () => {
    if (exporting || !navigator.share) return;
    setExporting(true);
    setExportError(null);
    try {
      const result = await runBake();
      const fileObj = new File([result.blob], result.fileName, {
        type: "application/pdf",
      });
      await navigator.share({
        files: [fileObj],
        title: result.fileName,
      });
      completeFinishAction(SIGN_PDF_FINISH_THANK_YOU_ACTIONS.SHARE);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setExportError(err?.message || "Could not share PDF.");
      }
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const handleMarkPageDone = (p) => {
    pendingReviewAfterDoneRef.current = true;
    if (pageScope === SIGN_PDF_SCOPES.CURRENT) {
      setPageConfirmedMap((prev) => ({ ...prev, [String(p)]: true }));
      setAllPagesConfirmed(true);
      setPopdownReopenToken((t) => t + 1);
      return;
    }
    setPageConfirmedMap((prev) => {
      const next = { ...prev, [String(p)]: true };
      if (pageScope === SIGN_PDF_SCOPES.PER_PAGE) {
        let n = p + 1;
        while (n <= cappedPageCount && next[String(n)]) n += 1;
        if (n <= cappedPageCount) {
          goToPage(n);
        }
      }
      return next;
    });
    setPopdownReopenToken((t) => t + 1);
  };

  const handleMarkAllDone = () => {
    pendingReviewAfterDoneRef.current = true;
    setAllPagesConfirmed(true);
    setPopdownReopenToken((t) => t + 1);
  };

  useEffect(() => {
    if (
      !pendingReviewAfterDoneRef.current ||
      !allPagesComplete ||
      inReview
    ) {
      return;
    }
    pendingReviewAfterDoneRef.current = false;
    setInReview(true);
    setCompareMode("preview");
  }, [allPagesComplete, inReview]);

  useEffect(() => {
    setPageConfirmedMap({});
    setAllPagesConfirmed(false);
  }, [session.pageScope, session.signingIntentChosen]);

  useEffect(() => {
    if (placementCount !== prevPlacementCountRef.current) {
      setPopdownReopenToken((t) => t + 1);
    }
    prevPlacementCountRef.current = placementCount;
  }, [placementCount]);

  const handleGoReview = () => {
    setInReview(true);
    setCompareMode("preview");
  };

  const handleGoFinish = () => {
    if (
      !hasAnySignPdfMark(session) &&
      !(session?.placements || []).length
    ) {
      setExportError("Add at least one signature or field before finishing.");
      return;
    }
    setFinishOpen(true);
  };

  useEffect(() => {
    if (hasPreview && setupChecklistComplete && !autoOpenFields) {
      setAutoOpenFields(true);
    }
  }, [hasPreview, setupChecklistComplete, autoOpenFields]);

  const workflowHint = signPdfWorkflowHint(workflowStep, session, {
    currentPage: pageNum,
  });
  const progressHint =
    exporting && exportProgress
      ? pageScope === SIGN_PDF_SCOPES.CURRENT
        ? `Signing page ${pageNum}…`
        : pageScope === SIGN_PDF_SCOPES.PER_PAGE
          ? `Signing customized pages… (${exportProgress.page || 1}/${exportProgress.total})`
          : `Signing page ${exportProgress.page || 1} of ${exportProgress.total}…`
      : exportError
        ? exportError
        : exportSuccess
          ? `Downloaded ${exportSuccess}. You can adjust and download again or try a new document.`
          : pageCount > PDF_WATERMARK_MAX_PAGES
            ? `${workflowHint} First ${PDF_WATERMARK_MAX_PAGES} pages only.`
            : workflowHint;

  const toolbarDisabled = !previewUrl || loadingPreview || exporting;
  const isCheckbox =
    activePlacement?.kind === SIGN_PDF_FIELD_KINDS.CHECKBOX;
  const isDateOrText =
    activePlacement?.kind === SIGN_PDF_FIELD_KINDS.DATE ||
    activePlacement?.kind === SIGN_PDF_FIELD_KINDS.TEXT;
  const isSignatureKind =
    activePlacement?.kind === SIGN_PDF_FIELD_KINDS.SIGNATURE ||
    activePlacement?.kind === SIGN_PDF_FIELD_KINDS.INITIALS;
  const isFieldKind = isDateOrText || isCheckbox;
  const showSignatureToolbar =
    Boolean(previewUrl) &&
    Boolean(activePlacement) &&
    Boolean(activeMark) &&
    isSignatureKind &&
    !inReview;
  const showFieldToolbar =
    Boolean(previewUrl) &&
    Boolean(activePlacement) &&
    (Boolean(activeMark) || isDateOrText) &&
    isFieldKind &&
    !inReview;

  const shareSupported =
    typeof navigator !== "undefined" &&
    typeof navigator.share === "function" &&
    typeof navigator.canShare === "function";

  const leftContent = (
    <div className="sp-panel">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sp-file-input"
        onChange={onInputChange}
      />
      <div className="sp-left-scroll tool-prep-scroll" ref={scrollBodyRef}>
        <div className="sp-left-scroll__source">
          <DocumentFileSourceCard
            fileName={fileName}
            fileSizeLabel={fileSizeLabel}
            pageCount={pageCount}
            acceptLabel="PDF"
            kind="pdf"
            hint="Supported formats: PDF"
            loading={loadingPreview && !exporting}
            error={error}
            onUploadClick={openPicker}
            onFiles={onDropFiles}
            disabled={loadingPreview || exporting}
            uploadProgress={uploadProgress}
            hideFilledMeta={Boolean(previewUrl && uploadProgress == null)}
          />
          {previewUrl && uploadProgress == null ? (
            <ToolPrepSourceCards
              compareOnly
              selectedKey={compareMode}
              onSelect={setCompareMode}
              originalImageUrl={previewUrl}
              previewImageUrl={previewUrl}
              watermark={activeMark}
              MarksComponent={SignPdfThumbMarks}
              cutoutPending={loadingPage}
              sourceName={fileName}
              sourceSizeLabel={fileSizeLabel}
              sourceMetaExtra={
                pageCount > 0
                  ? `${pageCount} page${pageCount === 1 ? "" : "s"}`
                  : null
              }
              onUploadClick={openPicker}
              onRemove={handleClear}
              actionsDisabled={loadingPreview || exporting}
            />
          ) : null}
        </div>
        <div className="sp-controls">
          <SignPdfControls
            key={controlsKey}
            session={session}
            onSessionChange={setSession}
            disabled={toolbarDisabled}
            currentPage={pageNum}
            autoOpen={hasPreview}
            hasDocument={hasPreview}
            pageCount={pageCount}
            fileSizeLabel={fileSizeLabel}
            workflowStep={workflowStep}
            reviewComplete={reviewMarkedComplete}
            onOpenFinish={() => setFinishOpen(true)}
            onSignaturePlaced={() => {
              goToPage(1);
              setPopdownReopenToken((t) => t + 1);
            }}
            onActionsReady={(actions) => {
              actionsRef.current = actions;
            }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ToolPrepPopup
        toolKey="sign_pdf"
        title="Sign PDF"
        left={leftContent}
        bodyOwnsScroll
        busy={exporting}
        hasSource={Boolean(hasFile || loadingPreview || previewUrl)}
        preview={
          <div className="sp-preview">
            {hasPreview ? (
              <SignPdfSignToolbar
                session={session}
                workflowStep={workflowStep}
                disabled={toolbarDisabled}
                pageCount={pageCount}
                currentPage={pageNum}
                maxPages={PDF_WATERMARK_MAX_PAGES}
                pageUrls={pageUrls}
                pageConfirmedMap={pageConfirmedMap}
                allPagesConfirmed={allPagesConfirmed}
                allPagesComplete={allPagesComplete}
                onSelectPage={(n) => {
                  setInReview(false);
                  goToPage(n);
                }}
                onPlaceField={placeField}
                onPlaceBlock={(block, sizeId, meta) =>
                  actionsRef.current?.placeBlock?.(block, sizeId, meta)
                }
                onBlockLocked={() => {}}
                onBlocksLocked={() =>
                  actionsRef.current?.openSignature?.()
                }
                onEditSignature={() =>
                  actionsRef.current?.openSignature?.()
                }
                onEditInitials={() => actionsRef.current?.openInitials?.()}
                onChooseFormat={() => actionsRef.current?.openFormat?.()}
                onMarkPageDone={handleMarkPageDone}
                onMarkAllDone={handleMarkAllDone}
                onGoReview={handleGoReview}
                onFinishDownload={handleDownload}
                onFinishDownloadLater={handleDownloadLater}
                onFinishPrint={handlePrint}
                onFinishEmailPdf={handleEmailPdf}
                onFinishShare={handleShare}
                finishCompleted={finishCompleted}
                reviewMarkedComplete={reviewMarkedComplete}
                onReviewMarkedComplete={setReviewMarkedComplete}
                shareSupported={shareSupported}
                busy={exporting}
                canPlaceField={(kind) =>
                  actionsRef.current?.canPlaceField?.(kind) ?? false
                }
                canPlaceBlock={() =>
                  actionsRef.current?.canPlaceBlock?.() ?? false
                }
                autoOpenFields={autoOpenFields}
                ensurePageUrl={ensurePageUrl}
                popdownReopenToken={popdownReopenToken}
                placementDragging={placementDragging}
                getPageRailState={getPageRailState}
              />
            ) : null}
            <div className="sp-preview__body">
              <div className="sp-preview__main sp-preview-shell">
                <div className="sp-preview-toolbar-slot">
                  <SignPdfSignatureToolbar
                    session={session}
                    onSessionChange={setSession}
                    currentPage={pageNum}
                    visible={showSignatureToolbar}
                    disabled={toolbarDisabled}
                    previewStageSize={previewStageSize}
                  />
                  <SignPdfFieldToolbar
                    session={session}
                    onSessionChange={setSession}
                    currentPage={pageNum}
                    visible={showFieldToolbar}
                    disabled={toolbarDisabled}
                    previewStageSize={previewStageSize}
                  />
                </div>
                <SignPdfPreview
                  imageUrl={previewUrl}
                  loading={loadingPreview || loadingPage}
                  loadingLabel={
                    loadingPreview
                      ? "Rendering PDF…"
                      : `Loading page ${currentPage}…`
                  }
                  previewBg={TOOL_PREP_PREVIEW_BG.ORIGINAL}
                  session={session}
                  currentPage={pageNum}
                  onSessionChange={setSession}
                  onFieldDrop={(kind) => placeField(kind)}
                  onSigningBlockDrop={(payload) => placeSigningBlock(payload)}
                  onPlacementDragChange={setPlacementDragging}
                  onStageSizeChange={setPreviewStageSize}
                  compareMode={compareMode}
                  emptyLabel={
                    uploadProgress != null && !previewUrl
                      ? TOOL_PREP_PREPARING_PREVIEW_LABEL
                      : undefined
                  }
                  stageInset={
                    previewUrl
                      ? DOCUMENT_PREP_STAGE_INSET
                      : TOOL_PREP_STAGE_INSET
                  }
                />
              </div>
              <DocumentPageThumbnails
                pageCount={pageCount}
                pageUrls={pageUrls}
                currentPage={currentPage}
                onSelect={goToPage}
                ensurePageUrl={ensurePageUrl}
                disabled={exporting}
                maxPages={PDF_WATERMARK_MAX_PAGES}
                getPageWatermark={(n) =>
                  resolveSignPdfMarksForPage(session, n, pageNum)
                }
                getPageStatus={(n) => getSignPdfPageSigningStatus(session, n)}
                MarksComponent={SignPdfThumbMarks}
                showMarks={compareMode !== "original"}
              />
            </div>
          </div>
        }
        onDone={handleDone}
        onCancel={onCancel}
        confirmClose={Boolean(hasFile)}
        doneDisabled={!hasFile || loadingPreview}
        doneLabel="Finish"
        footerHint={progressHint}
        footerHintClassName={
          exportError
            ? "sp-footer-error"
            : exportSuccess
              ? "sp-footer-success"
              : ""
        }
      />

      <SignPdfFinishModal
        open={finishOpen}
        onClose={() => setFinishOpen(false)}
        onDownload={handleDownload}
        onDownloadLater={handleDownloadLater}
        onPrint={handlePrint}
        onEmailPdf={handleEmailPdf}
        onShare={handleShare}
        busy={exporting}
        shareSupported={shareSupported}
      />

      <SignPdfFinishThankYouModal
        open={Boolean(finishThankYouAction)}
        action={finishThankYouAction}
        onClose={() => setFinishThankYouAction(null)}
        onSignAnother={() => {
          setFinishThankYouAction(null);
          handleClear();
        }}
        onBackToDashboard={() => {
          setFinishThankYouAction(null);
          onCancel();
        }}
      />
    </>
  );
};

export default SignPdfToolPrep;
